import React, { useState } from "react";
import { Handle, Position } from "react-flow-renderer";
import {
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
} from "@mui/material";
import { useTheme, alpha, darken } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";

const CustomNode = ({ data, selected }) => {
    const theme = useTheme();
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [nodeData, setNodeData] = useState(data);
    const [isEditing, setIsEditing] = useState(false);

    const handleSave = () => {
        if (data.onEdit) {
            data.onEdit(nodeData);
        }
        setEditModalOpen(false);
    };

    const isTextNode = data.type === "text";
    const isDefaultNode = data.type === "defaultNode";
    const handleColor = alpha(theme.palette.common.white, 0.35);

    const shell = (bg, borderColor, fg) => ({
        padding: "14px 16px",
        borderRadius: `${theme.shape.borderRadius}px`,
        background: bg,
        textAlign: "center",
        color: fg,
        minWidth: 240,
        maxWidth: 300,
        position: "relative",
        boxShadow: selected ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.65)}` : "none",
        border: `1px solid ${borderColor}`,
    });

    let rootStyle;
    if (isDefaultNode) {
        rootStyle = shell(
            theme.palette.primary.main,
            darken(theme.palette.primary.main, 0.15),
            theme.palette.primary.contrastText
        );
    } else if (isTextNode) {
        rootStyle = shell(
            darken(theme.palette.secondary.main, 0.12),
            alpha(theme.palette.secondary.light, 0.5),
            theme.palette.common.white
        );
    } else {
        rootStyle = shell(
            theme.palette.error.main,
            darken(theme.palette.error.main, 0.12),
            theme.palette.error.contrastText
        );
    }

    const iconColor = rootStyle.color;

    return (
        <div style={rootStyle}>
            <Handle type="target" position={Position.Top} id="top" style={{ background: handleColor, width: 10, height: 10 }} />
            <Handle type="source" position={Position.Right} id="right" style={{ background: handleColor, width: 10, height: 10 }} />
            <Handle type="source" position={Position.Bottom} id="bottom" style={{ background: handleColor, width: 10, height: 10 }} />
            <Handle type="target" position={Position.Left} id="left" style={{ background: handleColor, width: 10, height: 10 }} />

            {isDefaultNode ? (
                <>
                    <div style={{ position: "absolute", top: 6, right: 6 }}>
                        <IconButton size="small" onClick={() => setEditModalOpen(true)} sx={{ color: iconColor }}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </div>
                    <strong style={{ fontSize: "1.25rem", display: "block", marginBottom: 10 }}>
                        {nodeData.label || "System Title"}
                    </strong>
                    <Box
                        sx={{
                            fontSize: 14,
                            my: 1.25,
                            p: 1.25,
                            borderRadius: 1,
                            backgroundColor: alpha(theme.palette.common.black, 0.15),
                            color: "inherit",
                        }}
                    >
                        {nodeData.content || "Describe your system here."}
                    </Box>
                </>
            ) : isTextNode ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                    {isEditing ? (
                        <TextField
                            value={nodeData.label}
                            onChange={(e) => setNodeData({ ...nodeData, label: e.target.value })}
                            size="small"
                            fullWidth
                            variant="outlined"
                        />
                    ) : (
                        <strong style={{ flex: 1, textAlign: "left" }}>{nodeData.label || "Edit me"}</strong>
                    )}
                    {isEditing ? (
                        <IconButton size="small" onClick={handleSave} sx={{ color: theme.palette.success.light }}>
                            <SaveIcon fontSize="small" />
                        </IconButton>
                    ) : (
                        <IconButton size="small" onClick={() => setIsEditing(true)} sx={{ color: iconColor }}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    )}
                </div>
            ) : (
                <>
                    <div style={{ position: "absolute", top: 6, right: 6 }}>
                        <IconButton size="small" onClick={() => setEditModalOpen(true)} sx={{ color: iconColor }}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </div>
                    <Box sx={{ mb: 1, pb: 0.75, borderBottom: `1px solid ${alpha(iconColor, 0.45)}` }}>
                        <strong style={{ fontSize: "1.1rem" }}>{nodeData.label || "Untitled"}</strong>
                    </Box>
                    <p style={{ fontSize: 12, margin: 0, opacity: 0.95 }}>{nodeData.content || "No description available."}</p>
                </>
            )}

            <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Node</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Title"
                        value={nodeData.label}
                        onChange={(e) => setNodeData({ ...nodeData, label: e.target.value })}
                        margin="normal"
                    />
                    <TextField
                        fullWidth
                        label="Description"
                        value={nodeData.content}
                        onChange={(e) => setNodeData({ ...nodeData, content: e.target.value })}
                        margin="normal"
                        multiline
                        rows={3}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditModalOpen(false)}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" color="primary">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default CustomNode;
